// Remembers where a visitor ENTERED from (the referrer and the `utm_*` campaign
// tags), so a guest session created later can record it (#3530, D-185 b).
//
// Why this exists: when Navigator drop-off moved after 24 August nobody could
// say which Yoma door the new audience used. The server stores the value on the
// guest row (`users.guest_entry_source`, migration 0474) and
// `scripts/yoma-evidence.mjs --entry-source` reads it.
//
// CAPTURED AT BOOT, NOT AT MINT. By the time `loginAsGuest` runs the query
// string is usually gone (the visitor clicked on) and `document.referrer` is the
// app itself. Same reason as landingAudience.js, which this follows.
//
// THE SERVER IS AUTHORITATIVE; THIS IS DEFENCE IN DEPTH. A curl caller bypasses
// this file, so backend/src/services/guestEntrySource.js re-parses and
// re-sanitises everything. This copy exists so a token never reaches
// sessionStorage, memory or the network in the first place (the #3173 / #3282
// URL-leak class). The credential rules below are a COPY of the backend's (this
// bundle cannot import backend code); one shared case table drives both tests.
//
// WHAT IS READ: `document.referrer` and ONLY the five `utm_*` keys of
// `location.search`. `token`, `email` and every other param are never read.
//
// SHAPE (the snapshot, the sessionStorage value and the request body field
// `entrySource` are all this one object):
//   {"v":1,"referrer":"https://yoma.world/opportunities"|null,"utm":{"utm_source":"x"}}
// `referrer` is origin + pathname only (query, fragment, userinfo, port dropped;
// credential-shaped path segments replaced). The literal "[redacted]" means the
// HOST looked credential-shaped.
//
// ABSENCE: the frontend ALWAYS sends an explicit object, even when empty
// (`referrer: null, utm: {}` = "arrived direct"). The server stores SQL NULL
// only when nothing was sent at all, so "direct" and "not captured" never merge.

const STORAGE_KEY = 'tacit_entry_source';
const VERSION = 1;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const REDACTED = '[redacted]';
const MAX_UTM_VALUE_LENGTH = 100;
const MAX_PATH_LENGTH = 200;
const MAX_DECODE_PASSES = 3;

// --- credential rules (COPY of backend/src/services/guestEntrySource.js) -----
// `i` on the JWT run: a hostname is lowercased by the URL parser.
const JWT_RUN = /eyj[\w-]{8,}\.[\w-]{8,}\.?[\w-]*/i;
const HEX_RUN = /[0-9a-f]{32,}/i;
const BASE64URL_RUN = /[A-Za-z0-9_-]{40,}/;
const EMAIL_AT = /@/;

function matchesCredentialShape(text) {
  return JWT_RUN.test(text) || HEX_RUN.test(text) || BASE64URL_RUN.test(text) || EMAIL_AT.test(text);
}

/**
 * True if `text` contains, ANYWHERE and after repeated percent-decoding, a
 * credential-shaped run (a JWT, 32+ hex, 40+ base64url, or an `@`). A decode
 * error is suspicious, and so is text still decoding after three passes.
 * Substring search on purpose: `campaign:eyJ...` must be caught.
 */
export function containsCredential(text) {
  let current = String(text ?? '');
  if (matchesCredentialShape(current)) return true;
  for (let pass = 0; pass <= MAX_DECODE_PASSES; pass += 1) {
    let next;
    try {
      next = decodeURIComponent(current);
    } catch {
      return true;
    }
    if (next === current) return false;
    if (pass === MAX_DECODE_PASSES) return true;
    current = next;
    if (matchesCredentialShape(current)) return true;
  }
  return false;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g;

// COPY of backend/src/services/guestEntrySource.js truncateWellFormed. A bare
// `slice` can leave the high half of an emoji at the cut; JSON.stringify writes
// that lone half as `\ud83d`, which PostgreSQL's jsonb rejects, failing the
// guest INSERT. Lone halves are replaced with U+FFFD, a pair is never split.
// No lookbehind (old iOS Safari throws a SyntaxError at module parse).
const SURROGATE_UNIT = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g;
const REPLACEMENT_CHAR = '�';

export function truncateWellFormed(text, max) {
  const wellFormed = String(text).replace(SURROGATE_UNIT, (m) => (m.length === 2 ? m : REPLACEMENT_CHAR));
  if (wellFormed.length <= max) return wellFormed;
  const cut = wellFormed.slice(0, max);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

// ORDER: strip controls, then DECODE+DETECT, then truncate. Truncating first
// could clip a token below the run-length threshold.
function sanitizeUtmValue(raw) {
  if (typeof raw !== 'string') return null;
  const cleaned = raw.replace(CONTROL_CHARS, '').trim();
  if (cleaned === '') return null;
  if (containsCredential(cleaned)) return REDACTED;
  return truncateWellFormed(cleaned, MAX_UTM_VALUE_LENGTH);
}

function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Mirrors lib/auth/magicLinkUrlScrub.js classifySecretPath (case-insensitive,
// each segment decoded on its own). Returns the harmless stub path to send in
// place of a secret-bearing one, or null if the path is not secret.
function secretPathStub(pathname) {
  const norm = String(pathname ?? '').split('/').map((s) => decodeSegment(s).toLowerCase());
  if (norm[0] === '' && norm[1] === 'unsubscribe') return '/unsubscribe';
  if (norm[0] === '' && norm[1] === 'auth' && norm[2] === 'verify') return '/auth/verify';
  return null;
}

/**
 * Reduce a referrer URL to `scheme://host/path` (no port, query, fragment or
 * userinfo), with credential-shaped path segments replaced. Returns null for
 * anything that is not an http(s) URL, and the literal "[redacted]" when the
 * host itself looks like a credential. Pure.
 */
export function reduceReferrer(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const host = url.hostname.toLowerCase();
  if (host === '') return null;
  if (containsCredential(host) || host.split('.').some((label) => containsCredential(label))) {
    return REDACTED;
  }
  const stub = secretPathStub(url.pathname);
  const path = stub !== null
    ? stub
    : url.pathname
      .split('/')
      .map((seg) => (containsCredential(seg) ? REDACTED : seg))
      .join('/')
      .slice(0, MAX_PATH_LENGTH);
  return `${url.protocol}//${host}${path || '/'}`;
}

/**
 * The five `utm_*` params of a query string, sanitised, keys exactly as the
 * lowercased `utm_*` names. Reads NOTHING else (never `token` or `email`).
 */
export function reduceUtm(search) {
  const out = {};
  let params;
  try {
    params = new URLSearchParams(String(search ?? ''));
  } catch {
    return out;
  }
  for (const key of params.keys()) {
    const lower = key.toLowerCase();
    if (!UTM_KEYS.includes(lower) || Object.prototype.hasOwnProperty.call(out, lower)) continue;
    const value = sanitizeUtmValue(params.get(key));
    if (value !== null) out[lower] = value;
  }
  return out;
}

function isExternalReferrer(referrer, ownHost) {
  if (typeof referrer !== 'string' || referrer === '') return false;
  try {
    return new URL(referrer).host !== ownHost;
  } catch {
    return false;
  }
}

/**
 * Build the snapshot for ONE document load. An own-host referrer (internal
 * navigation) is not an entry: `referrer` is null for it.
 */
export function buildSnapshot({ referrer, search, ownHost }) {
  return {
    v: VERSION,
    referrer: isExternalReferrer(referrer, ownHost) ? reduceReferrer(referrer) : null,
    utm: reduceUtm(search),
  };
}

// A value read back from sessionStorage is re-validated and re-reduced: storage
// is not trusted (an extension or an older build could have written anything).
function normaliseStored(raw) {
  if (raw === null || typeof raw !== 'string') return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  if (parsed.v !== VERSION) return null;
  if (parsed.referrer !== null && typeof parsed.referrer !== 'string') return null;
  if (!parsed.utm || typeof parsed.utm !== 'object' || Array.isArray(parsed.utm)) return null;
  const utm = {};
  for (const key of UTM_KEYS) {
    const value = Object.prototype.hasOwnProperty.call(parsed.utm, key)
      ? sanitizeUtmValue(parsed.utm[key])
      : null;
    if (value !== null) utm[key] = value;
  }
  const referrer = parsed.referrer === REDACTED ? REDACTED : reduceReferrer(parsed.referrer);
  return { v: VERSION, referrer, utm };
}

function storageRead(win = (typeof window !== 'undefined' ? window : undefined)) {
  try {
    return win.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function storageWrite(value, win = (typeof window !== 'undefined' ? window : undefined)) {
  try {
    win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore: the module-memory snapshot is the primary source; storage only
    // carries it across a full document reload.
  }
}

// PRIMARY SOURCE. sessionStorage can throw (Safari private mode, blocked site
// data) and landingAudience's safeWrite swallows that, so a storage-only design
// loses the boot capture whenever storage fails (plan check finding 7).
let memorySnapshot = null;

/**
 * Decide this document load's snapshot and remember it. Overwrite rule: replace
 * what is held only if THIS load carries a `utm_*` param or an EXTERNAL
 * referrer; otherwise keep it, so internal navigation (referrer = the app)
 * cannot erase the real entry. Exported for tests.
 */
export function captureEntrySource(win = (typeof window !== 'undefined' ? window : undefined),
  doc = (typeof document !== 'undefined' ? document : undefined)) {
  if (!win || !doc) return null;
  const fresh = buildSnapshot({
    referrer: doc.referrer,
    search: win.location?.search,
    ownHost: win.location?.host,
  });
  const replaces = fresh.referrer !== null || Object.keys(fresh.utm).length > 0;
  const held = memorySnapshot || normaliseStored(storageRead(win));
  memorySnapshot = replaces || !held ? fresh : held;
  storageWrite(memorySnapshot, win);
  return memorySnapshot;
}

/**
 * The snapshot to send as the `entrySource` body field, or null if capture
 * never ran (no window). In a browser this is always an object, even an empty
 * one ("arrived direct"), never absent.
 */
export function getEntrySource() {
  try {
    if (memorySnapshot) return memorySnapshot;
    return normaliseStored(storageRead());
  } catch {
    return null; // the body then omits the field and the server stores NULL
  }
}

/** Test seam: forget the in-memory snapshot (not storage). */
export function __resetEntrySourceForTests() {
  memorySnapshot = null;
}

// Capture the ENTRY url once at boot (see the header). This module is imported
// by AuthContext at app start, so a throw here would take the whole app down
// over a telemetry field: swallow it. The cost is one guest with no captured
// source (the server stores NULL), which the reader counts as "(not captured)".
try {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    captureEntrySource(window, document);
  }
} catch {
  // never block boot on entry-source capture
}
